import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { CreditCard, Search, RefreshCw, CheckCircle, XCircle, Clock, Download, Eye, Filter, ChevronLeft, ChevronRight, RotateCw, AlertCircle, Copy, ExternalLink, Trash2, ArrowUp, ArrowDown, ArrowUpDown, FileSpreadsheet, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnlinePaymentReport = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { selectedBranch } = useApp();
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Role-based access: kepala_cabang & owner READ-ONLY, admin & super_admin can manage
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const userRole = String(currentUser?.role || '').toLowerCase();
  const canManage = ['admin', 'super_admin', 'superadmin'].includes(userRole);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [lastAutoSync, setLastAutoSync] = useState(null);
  // 2026-02 DYNAMIC_GATEWAY_LABEL: detect tenant's active gateway to show correct Sync button + labels
  const [activeGateway, setActiveGateway] = useState('duitku');
  const gwLabel = activeGateway === 'tripay' ? 'TriPay' : (activeGateway === 'xendit' ? 'Xendit' : 'Duitku');

  const handleSort = (key) => {
    setSortConfig(prev => prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' });
  };
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="inline w-3 h-3 ml-1 text-slate-400 dark:text-slate-500" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="inline w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />
      : <ArrowDown className="inline w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />;
  };

  // Webhook URL
  const webhookUrl = `${API_URL}/api/payments/xendit/webhook`;

  const viewPaymentDetail = (payment) => {
    setSelectedPayment(payment);
    setShowDetailModal(true);
  };

  // Delete payment
  const deletePayment = async (paymentId) => {
    if (!window.confirm('Yakin ingin menghapus data pembayaran ini?')) return;
    
    try {
      setDeletingId(paymentId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/online-payments/${paymentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Pembayaran berhasil dihapus');
        setPayments(prev => prev.filter(p => p.id !== paymentId));
        setFilteredPayments(prev => prev.filter(p => p.id !== paymentId));
        setSelectedIds(prev => { const s = new Set(prev); s.delete(paymentId); return s; });
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Gagal menghapus pembayaran');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    // 2026-08-08 FIX: AbortController pattern
    const controller = new AbortController();
    fetchPayments({ signal: controller.signal });
    return () => controller.abort();
  }, [selectedBranch]);

  // 2026-02 DYNAMIC_GATEWAY_LABEL: load active_gateway from tenant payment-gateway config
  useEffect(() => {
    const loadGateway = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(API_URL + '/api/v2/payment-gateway/config', { headers: { Authorization: 'Bearer ' + token } });
        const g = String(r?.data?.data?.active_gateway || r?.data?.active_gateway || 'duitku').toLowerCase();
        setActiveGateway(g === 'tripay' || g === 'xendit' || g === 'duitku' ? g : 'duitku');
      } catch (e) { /* silent, fallback to duitku */ }
    };
    loadGateway();
  }, []);

  useEffect(() => {
    if (!canManage) return;
    const fetchAutoSync = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API_URL}/api/v2/online-payments/duitku-sync-history?limit=1`, {
          headers: { Authorization: 'Bearer ' + token }
        });
        const first = (resp.data?.history || []).find(h => h.sync_type === 'duitku_daily_auto');
        if (first) setLastAutoSync(first);
      } catch (e) { /* silent */ }
    };
    fetchAutoSync();
  }, []);

  useEffect(() => {
    filterPayments();
  }, [payments, searchTerm, statusFilter, channelFilter, sortConfig]);

  const syncGatewayPayments = async () => {
    // 2026-02 DYNAMIC_GATEWAY_LABEL: dispatch to sync-tripay | sync-duitku based on active gateway
    const gw = activeGateway === 'tripay' ? 'tripay' : 'duitku';
    const label = gw === 'tripay' ? 'TriPay' : 'Duitku';
    try {
      setSyncing(true);
      const token = localStorage.getItem('token');
      const resp = await axios.post(`${API_URL}/api/v2/online-payments/sync-${gw}`, {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (resp.data.success) {
        const { checked = 0, updated = 0, failed_ghost = 0 } = resp.data;
        const parts = [];
        if (updated > 0) parts.push(`${updated} pending menjadi LUNAS`);
        if (failed_ghost > 0) parts.push(`${failed_ghost} ghost (kadaluwarsa di ${label}) → FAILED`);
        if (parts.length > 0) {
          toast.success(`✅ Sync ${label}: ${parts.join(' • ')}. Total ${checked} pending diperiksa.`, { duration: 10000 });
        } else if (checked > 0) {
          toast.info(`ℹ️ Sync selesai. ${checked} pending diperiksa — semua masih belum bayar di ${label}.`);
        } else {
          toast.info('ℹ️ Tidak ada pembayaran pending untuk di-sync.');
        }
        await fetchPayments();
      } else {
        toast.error(`Sync ${label} gagal: ` + (resp.data.detail || resp.data.error || 'Unknown error'));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `Sync ${label} gagal: ` + e.message);
    } finally {
      setSyncing(false);
    }
  };

  const fetchPayments = async ({ signal = null } = {}) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== 'all') params.append('branch_id', selectedBranch);
      const qs = params.toString();
      const url = `${API_URL}/api/v2/online-payments/report${qs ? '?' + qs : ''}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (response.data.success) {
        setPayments(response.data.data);
      }
    } catch (error) {
      // 2026-08-08: silent ignore aborted request
      if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching payments:', error);
      toast.error('Gagal memuat data pembayaran');
    } finally {
      setLoading(false);
    }
  };

  const syncAllPayments = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/online-payments/sync-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        fetchPayments();
      }
    } catch (error) {
      console.error('Error syncing payments:', error);
      toast.error(error.response?.data?.detail || 'Gagal sync pembayaran');
    } finally {
      setSyncing(false);
    }
  };

  const syncSinglePayment = async (paymentId) => {
    try {
      setSyncingId(paymentId);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/online-payments/sync-xendit/${paymentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        fetchPayments();
      }
    } catch (error) {
      console.error('Error syncing payment:', error);
      toast.error(error.response?.data?.detail || 'Gagal sync pembayaran');
    } finally {
      setSyncingId(null);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success('Webhook URL disalin!');
  };

  const filterPayments = () => {
    let filtered = [...payments];

    // Search filter
    if (searchTerm) {
      const search = String(searchTerm).toLowerCase();
      // 2026-08-08 FIX: wrap semua field dgn String() supaya angka (customer_id INT, dsb)
      // tidak crash .toLowerCase() — sebelumnya bikin React bluescreen saat search.
      filtered = filtered.filter(p =>
        String(p.customer_name || '').toLowerCase().includes(search) ||
        String(p.invoice_number || '').toLowerCase().includes(search) ||
        String(p.voucher_code || '').toLowerCase().includes(search) ||
        String(p.payment_code || '').toLowerCase().includes(search) ||
        String(p.customer_id || '').toLowerCase().includes(search) ||
        String(p.account_number || '').toLowerCase().includes(search)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      const STATUS_SYNONYMS = {
        success:  ['success', 'paid', 'completed', 'settled'],
        pending:  ['pending', 'unpaid', 'waiting'],
        failed:   ['failed', 'fail', 'error'],
        expired:  ['expired', 'cancelled', 'canceled'],
      };
      const wanted = STATUS_SYNONYMS[statusFilter] || [statusFilter];
      filtered = filtered.filter(p => wanted.includes(String(p.status || '').toLowerCase()));
    }

    // Channel filter
    if (channelFilter !== 'all') {
      filtered = filtered.filter(p => p.payment_channel === channelFilter);
    }

    // Apply sort
    if (sortConfig.key) {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      filtered = [...filtered].sort((a, b) => {
        const key = sortConfig.key;
        let av, bv;
        if (key === 'amount') {
          av = Number(a.amount) || 0; bv = Number(b.amount) || 0;
        } else if (key === 'created_at') {
          av = new Date(a.created_at || 0).getTime();
          bv = new Date(b.created_at || 0).getTime();
        } else {
          av = (a[key] ?? '').toString().toLowerCase();
          bv = (b[key] ?? '').toString().toLowerCase();
        }
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }

    setFilteredPayments(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    if (['success', 'paid', 'completed', 'settled'].includes(s)) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-green-300 text-green-700 bg-white dark:text-green-300 dark:bg-slate-800">
          <CheckCircle className="w-3 h-3" /> LUNAS
        </span>
      );
    }
    if (['pending', 'unpaid', 'waiting'].includes(s)) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-yellow-300 text-yellow-700 bg-white dark:text-yellow-300 dark:bg-slate-800">
          <Clock className="w-3 h-3" /> PENDING
        </span>
      );
    }
    if (['failed', 'fail', 'error'].includes(s)) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-red-300 text-red-700 bg-white dark:text-red-300 dark:bg-slate-800">
          <XCircle className="w-3 h-3" /> GAGAL
        </span>
      );
    }
    if (['expired', 'cancelled', 'canceled'].includes(s)) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          <XCircle className="w-3 h-3" /> {s === 'expired' ? 'KEDALUWARSA' : 'DIBATALKAN'}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-gray-300 text-gray-700 bg-white dark:border-slate-600 dark:text-slate-200 dark:bg-slate-800">
        {status || 'Unknown'}
      </span>
    );
  };

  // Duitku 2-char channel code -> friendly name (matches Duitku's paymentName)
  const CHANNEL_META = {
    // QRIS variants
    'SP': { name: 'SHOPEEPAY QRIS', color: 'border border-orange-400 text-orange-700 dark:border-orange-500 dark:text-orange-300' },
    'SQ': { name: 'NUSAPAY QRIS',   color: 'border border-indigo-400 text-indigo-700 dark:border-indigo-500 dark:text-indigo-300' },
    'NQ': { name: 'NUSAPAY QRIS',   color: 'border border-indigo-400 text-indigo-700 dark:border-indigo-500 dark:text-indigo-300' },
    'SA': { name: 'SHOPEEPAY APP',  color: 'border border-orange-400 text-orange-700 dark:border-orange-500 dark:text-orange-300' },
    'LQ': { name: 'LINKAJA QRIS',   color: 'border border-red-400 text-red-700 dark:border-red-500 dark:text-red-300' },
    // E-Wallet
    'OV': { name: 'OVO',            color: 'border border-purple-400 text-purple-700 dark:border-purple-500 dark:text-purple-300' },
    'DA': { name: 'DANA',           color: 'border border-sky-400 text-sky-700 dark:border-sky-500 dark:text-sky-300' },
    'LA': { name: 'LINKAJA APPS',   color: 'border border-red-400 text-red-700 dark:border-red-500 dark:text-red-300' },
    'LF': { name: 'LINKAJA',        color: 'border border-red-400 text-red-700 dark:border-red-500 dark:text-red-300' },
    'AP': { name: 'ASTRAPAY',       color: 'border border-blue-400 text-blue-700 dark:border-blue-500 dark:text-blue-300' },
    // Virtual Account
    'BC': { name: 'BCA VA',         color: 'border border-blue-400 text-blue-700 dark:border-blue-500 dark:text-blue-300' },
    'BR': { name: 'BRI VA',         color: 'border border-blue-400 text-blue-700 dark:border-blue-500 dark:text-blue-300' },
    'BN': { name: 'BNI VA',         color: 'border border-orange-400 text-orange-700 dark:border-orange-500 dark:text-orange-300' },
    'M2': { name: 'MANDIRI VA',     color: 'border border-yellow-500 text-yellow-700 dark:border-yellow-500 dark:text-yellow-300' },
    'BT': { name: 'PERMATA VA',     color: 'border border-emerald-400 text-emerald-700 dark:border-emerald-500 dark:text-emerald-300' },
    'B1': { name: 'CIMB NIAGA VA',  color: 'border border-red-400 text-red-700 dark:border-red-500 dark:text-red-300' },
    'BJ': { name: 'BJB VA',         color: 'border border-purple-400 text-purple-700 dark:border-purple-500 dark:text-purple-300' },
    'BS': { name: 'BSI VA',         color: 'border border-green-400 text-green-700 dark:border-green-500 dark:text-green-300' },
    'SL': { name: 'SAMPOERNA VA',   color: 'border border-amber-400 text-amber-700 dark:border-amber-500 dark:text-amber-300' },
    'AG': { name: 'ARTHA GRAHA VA', color: 'border border-slate-400 text-slate-700 dark:border-slate-500 dark:text-slate-300' },
    'DM': { name: 'DANAMON VA',     color: 'border border-orange-400 text-orange-700 dark:border-orange-500 dark:text-orange-300' },
    'NC': { name: 'BNC VA',         color: 'border border-teal-400 text-teal-700 dark:border-teal-500 dark:text-teal-300' },
    'VA': { name: 'MAYBANK VA',     color: 'border border-yellow-500 text-yellow-700 dark:border-yellow-500 dark:text-yellow-300' },
    'A1': { name: 'ATM BERSAMA',    color: 'border border-slate-400 text-slate-700 dark:border-slate-500 dark:text-slate-300' },
    // Retail
    'FT': { name: 'ALFAMART',       color: 'border border-red-400 text-red-700 dark:border-red-500 dark:text-red-300' },
    'IR': { name: 'INDOMARET',      color: 'border border-red-400 text-red-700 dark:border-red-500 dark:text-red-300' },
    // Credit card
    'VC': { name: 'KARTU KREDIT',   color: 'border border-slate-400 text-slate-700 dark:border-slate-500 dark:text-slate-300' },
    // Fallback long names (Xendit / legacy DB rows)
    'BCA':       { name: 'BCA VA',      color: 'border border-blue-400 text-blue-700 dark:border-blue-500 dark:text-blue-300' },
    'BRI':       { name: 'BRI VA',      color: 'border border-blue-400 text-blue-700 dark:border-blue-500 dark:text-blue-300' },
    'BNI':       { name: 'BNI VA',      color: 'border border-orange-400 text-orange-700 dark:border-orange-500 dark:text-orange-300' },
    'MANDIRI':   { name: 'MANDIRI VA',  color: 'border border-yellow-500 text-yellow-700 dark:border-yellow-500 dark:text-yellow-300' },
    'BSI':       { name: 'BSI VA',      color: 'border border-green-400 text-green-700 dark:border-green-500 dark:text-green-300' },
    'PERMATA':   { name: 'PERMATA VA',  color: 'border border-emerald-400 text-emerald-700 dark:border-emerald-500 dark:text-emerald-300' },
    'ALFAMART':  { name: 'ALFAMART',    color: 'border border-red-400 text-red-700 dark:border-red-500 dark:text-red-300' },
    'INDOMARET': { name: 'INDOMARET',   color: 'border border-red-400 text-red-700 dark:border-red-500 dark:text-red-300' },
    'OVO':       { name: 'OVO',         color: 'border border-purple-400 text-purple-700 dark:border-purple-500 dark:text-purple-300' },
    'DANA':      { name: 'DANA',        color: 'border border-sky-400 text-sky-700 dark:border-sky-500 dark:text-sky-300' },
    'GOPAY':     { name: 'GOPAY',       color: 'border border-green-400 text-green-700 dark:border-green-500 dark:text-green-300' },
    'SHOPEEPAY': { name: 'SHOPEEPAY',   color: 'border border-orange-400 text-orange-700 dark:border-orange-500 dark:text-orange-300' },
    'QRIS':      { name: 'QRIS',        color: 'border border-indigo-400 text-indigo-700 dark:border-indigo-500 dark:text-indigo-300' },
    'CREDIT_CARD': { name: 'KARTU KREDIT', color: 'border border-slate-400 text-slate-700 dark:border-slate-500 dark:text-slate-300' },
    'transfer':  { name: 'TRANSFER',    color: 'border border-slate-400 text-slate-700 dark:border-slate-500 dark:text-slate-300' },
  };
  const getChannelBadge = (channel) => {
    if (!channel) return <span className="text-slate-400 dark:text-slate-500">-</span>;
    const key = String(channel).toUpperCase();
    const meta = CHANNEL_META[key] || CHANNEL_META[channel] || { name: channel, color: 'border border-slate-400 text-slate-700 dark:border-slate-500 dark:text-slate-300' };
    return (
      <span className={`inline-flex items-center justify-center min-w-[110px] px-2 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap ${meta.color}`} title={`Kode: ${channel}`}>
        {meta.name}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('id-ID', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Bulk selection helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleSelectAllPage = () => {
    const pageIds = paginatedPayments.map(p => p.id).filter(Boolean);
    setSelectedIds(prev => {
      const allSelected = pageIds.every(id => prev.has(id));
      const s = new Set(prev);
      if (allSelected) pageIds.forEach(id => s.delete(id));
      else pageIds.forEach(id => s.add(id));
      return s;
    });
  };
  const isAllPageSelected = paginatedPayments.length > 0 && paginatedPayments.every(p => selectedIds.has(p.id));

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Hapus ${ids.length} pembayaran terpilih? Aksi ini tidak dapat dibatalkan.`)) return;
    try {
      setBulkDeleting(true);
      const token = localStorage.getItem('token');
      const resp = await axios.post(`${API_URL}/api/v2/online-payments/bulk-delete`, { ids }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.data?.success) {
        toast.success(`Berhasil hapus ${resp.data.deleted} dari ${resp.data.requested} pembayaran`);
        const deletedSet = new Set(ids);
        setPayments(prev => prev.filter(p => !deletedSet.has(p.id)));
        setFilteredPayments(prev => prev.filter(p => !deletedSet.has(p.id)));
        setSelectedIds(new Set());
      } else {
        toast.error('Gagal bulk delete');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal bulk delete');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Data source for export: selected rows if any, else all filtered
  const getExportRows = () => {
    if (selectedIds.size > 0) return filteredPayments.filter(p => selectedIds.has(p.id));
    return filteredPayments;
  };
  const fmtRupiah = (n) => 'Rp ' + (Number(n) || 0).toLocaleString('id-ID');
  const exportColumns = [
    { label: 'ID', value: 'id' },
    { label: 'Status', value: 'status' },
    { label: 'Nama', value: 'customer_name' },
    { label: 'ID Pelanggan', value: 'account_number' },
    { label: 'Invoice', value: 'invoice_number' },
    { label: 'Paket', value: 'plan_name' },
    { label: 'Kode Voucher', value: (r) => r.voucher_code || '' },
    { label: 'Channel', value: (r) => (CHANNEL_META[String(r.payment_channel||'').toUpperCase()] || CHANNEL_META[r.payment_channel] || {}).name || r.payment_channel || '-' },
    { label: 'Payment Code', value: 'payment_code' },
    { label: 'Amount', value: (r) => fmtRupiah(r.amount) },
    { label: 'Created', value: 'created_at' },
    { label: 'Tenant', value: 'tenant_name' },
  ];

  const exportToExcel = async () => {
    const rows = getExportRows();
    if (rows.length === 0) { toast.error('Tidak ada data untuk di-export'); return; }
    try {
      const XLSX = await import('xlsx');
      // Build array of objects keyed by column label (preserves header order)
      const data = rows.map(r => {
        const obj = {};
        exportColumns.forEach(c => {
          let v = typeof c.value === 'function' ? c.value(r) : r[c.value];
          if (v == null) v = '';
          obj[c.label] = v;
        });
        return obj;
      });
      const headers = exportColumns.map(c => c.label);
      const ws = XLSX.utils.json_to_sheet(data, { header: headers });
      // Auto-fit column widths
      ws['!cols'] = headers.map(h => {
        const maxLen = Math.max(
          h.length,
          ...data.map(row => String(row[h] ?? '').length)
        );
        return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
      });
      // Freeze header row
      ws['!freeze'] = { xSplit: 0, ySplit: 1 };
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Online Payments');
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `online-payment-report-${stamp}.xlsx`);
      toast.success(`Export ${rows.length} baris ke Excel (.xlsx)`);
    } catch (e) {
      console.error('[exportToExcel] error', e);
      toast.error('Gagal export XLSX: ' + (e?.message || 'unknown'));
    }
  };

  const exportToPdf = () => {
    const rows = getExportRows();
    if (rows.length === 0) { toast.error('Tidak ada data untuk di-export'); return; }
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup diblokir browser'); return; }
    const tbl = `<table style="width:100%;border-collapse:collapse;font-size:11px;margin-top:10px">
      <thead><tr>${exportColumns.map(c => `<th style="border:1px solid #ccc;padding:6px;background:#f1f5f9;text-align:left">${c.label}</th>`).join('')}</tr></thead>
      <tbody>${rows.map(r => `<tr>${exportColumns.map(c => {
        let v = typeof c.value === 'function' ? c.value(r) : r[c.value];
        return `<td style="border:1px solid #ccc;padding:5px">${v ?? '-'}</td>`;
      }).join('')}</tr>`).join('')}</tbody>
    </table>`;
    const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const summary = `<div style="margin-top:12px;padding:10px;background:#f8fafc;border-radius:6px">
      <strong>Total transaksi:</strong> ${rows.length} &nbsp;|&nbsp;
      <strong>Total nominal:</strong> ${fmtRupiah(total)}
    </div>`;
    w.document.write(`<html><head><title>Online Payment Report</title></head>
      <body style="font-family:Arial,sans-serif;padding:20px">
        <h2 style="margin:0">Online Payment Report</h2>
        <p style="color:#666;margin:4px 0">Dicetak: ${new Date().toLocaleString('id-ID')}</p>
        ${summary}${tbl}
        <script>setTimeout(()=>window.print(),300)</script></body></html>`);
    w.document.close();
    toast.success(`Export ${rows.length} baris ke PDF (print dialog)`);
  };

  const uniqueChannels = [...new Set(payments.map(p => p.payment_channel).filter(Boolean))];

  // Period-based stats — Rupiah nominal, only SUCCESS payments
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
  const thisYearStart = new Date(now.getFullYear(), 0, 1);
  const isSuccess = (p) => ['success', 'paid', 'PAID', 'completed', 'COMPLETED'].includes(p.status);
  const paidAt = (p) => new Date(p.payment_date || p.updated_at || p.created_at || 0);
  const sum = (arr) => arr.reduce((s, p) => s + (p.amount || 0), 0);
  const successPayments = payments.filter(isSuccess);
  const stats = {
    total: payments.length,
    active: successPayments.length,
    inactive: payments.filter(p => ['pending', 'PENDING', 'failed', 'expired', 'EXPIRED'].includes(p.status)).length,
    thisMonth: sum(successPayments.filter(p => paidAt(p) >= thisMonthStart)),
    previousMonth: sum(successPayments.filter(p => { const d = paidAt(p); return d >= prevMonthStart && d <= prevMonthEnd; })),
    thisYear: sum(successPayments.filter(p => paidAt(p) >= thisYearStart)),
    totalAmount: sum(successPayments)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4 dark:bg-slate-700"></div>
          <div className="h-64 bg-slate-200 rounded dark:bg-slate-700"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6 bg-slate-50 dark:bg-slate-950 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Online Payment Report
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Laporan transaksi pembayaran online ({gwLabel})
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {canManage && (
          <Button
            onClick={syncGatewayPayments}
            variant="outline"
            className="flex items-center gap-2 border-blue-500 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
            disabled={syncing}
            data-testid={"sync-" + activeGateway + "-btn"}
          >
            {syncing ? <RotateCw className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
            Sync dari {gwLabel}
          </Button>
          )}
          {canManage && (
          <label
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm cursor-pointer border border-purple-500 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/30 ${syncing ? 'opacity-50 pointer-events-none' : ''}`}
            data-testid={"upload-" + activeGateway + "-report-btn"}
            title={"Upload " + gwLabel + " Merchant Report (XLSX) untuk auto-reconcile transaksi yang missing"}
          >
            <RotateCw className="w-4 h-4" />
            Upload {gwLabel} Report
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              disabled={syncing}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  setSyncing(true);
                  // Parse XLSX via SheetJS (loaded dynamically)
                  const XLSX = await import('xlsx');
                  const data = await file.arrayBuffer();
                  const wb = XLSX.read(data, { type: 'array' });
                  const ws = wb.Sheets[wb.SheetNames[0]];
                  const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
                  // 2026-02 DYNAMIC_GATEWAY_LABEL: extract refs matching Duitku (D...) OR TriPay (T...) pattern based on active gateway
                  const refs = new Set();
                  const isTripay = activeGateway === 'tripay';
                  const refPattern = isTripay ? /\bT[A-Z0-9]{10,30}\b/g : /D\d{5,7}[A-Z0-9]{15,25}/g;
                  rows.forEach((row) => {
                    Object.values(row).forEach((v) => {
                      const s = String(v || '').toUpperCase();
                      const match = s.match(refPattern);
                      if (match) match.forEach((r) => refs.add(r));
                    });
                  });
                  const refList = Array.from(refs);
                  if (refList.length === 0) {
                    toast.error(`Tidak ada ref ${gwLabel} (${isTripay ? 'T...' : 'D...'}) ditemukan di file`);
                    return;
                  }
                  toast.info(`Ditemukan ${refList.length} refs, memproses...`);
                  const token = localStorage.getItem('token');
                  const endpoint = isTripay ? 'reconcile-tripay' : 'reconcile-duitku';
                  const resp = await axios.post(
                    `${API_URL}/api/v2/online-payments/${endpoint}`,
                    { refs: refList },
                    { headers: { Authorization: 'Bearer ' + token } }
                  );
                  const d = resp.data || {};
                  toast.success(
                    `✅ Reconcile: ${d.imported || 0} diimport, ${d.already_exists || 0} sudah ada, ${d.orphan || 0} orphan (invoice hilang), ${d.query_failed || 0} gagal query`
                  );
                  await fetchPayments();
                } catch (err) {
                  toast.error(`Gagal reconcile ${gwLabel}: ` + (err.response?.data?.detail || err.message));
                } finally {
                  setSyncing(false);
                  e.target.value = '';
                }
              }}
            />
          </label>
          )}
          <Button
            onClick={fetchPayments}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button
            onClick={exportToExcel}
            variant="outline"
            className="flex items-center gap-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
            data-testid="export-excel-btn"
            title={selectedIds.size > 0 ? `Export ${selectedIds.size} terpilih` : 'Export semua data yang difilter'}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </Button>
          <Button
            onClick={exportToPdf}
            variant="outline"
            className="flex items-center gap-2 border-rose-500 text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/30"
            data-testid="export-pdf-btn"
            title={selectedIds.size > 0 ? `Export ${selectedIds.size} terpilih` : 'Export semua data yang difilter'}
          >
            <FileText className="w-4 h-4" />
            PDF
          </Button>
          {canManage && selectedIds.size > 0 && (
            <Button
              onClick={bulkDelete}
              disabled={bulkDeleting}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4" />
              {bulkDeleting ? 'Menghapus…' : `Hapus (${selectedIds.size})`}
            </Button>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-md p-3 flex items-start gap-2 dark:bg-emerald-900/30">
        <div className="text-emerald-600 mt-0.5 dark:text-emerald-400">
          <CheckCircle className="w-4 h-4" />
        </div>
        <div className="text-xs text-emerald-800 dark:text-emerald-200">
          <span className="font-semibold">INFO:</span> Setiap pembayaran EWALLET, QRIS, dan KARTU KREDIT mungkin memiliki kelebihan bayar sekitar IDR 1 — IDR 100 karena perhitungan biaya transaksi berdasarkan persentase.
        </div>
      </div>

      {/* Auto-sync info banner (admin only) */}
      {canManage && (
      <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-md p-3 flex items-start gap-2 dark:bg-indigo-900/30" data-testid="duitku-auto-sync-banner">
        <div className="text-indigo-600 mt-0.5 dark:text-indigo-400">
          <RotateCw className="w-4 h-4" />
        </div>
        <div className="text-xs text-indigo-800 dark:text-indigo-200">
          <span className="font-semibold">AUTO-SYNC:</span> Sistem otomatis menarik status pembayaran {gwLabel} setiap hari pukul <strong>08:00 WIB</strong>.
          {lastAutoSync ? (
            <span> Sinkronisasi terakhir: <strong>{new Date(lastAutoSync.timestamp).toLocaleString('id-ID')}</strong> — {lastAutoSync.checked || 0} diperiksa, <strong>{lastAutoSync.updated || 0}</strong> pembayaran terupdate.</span>
          ) : (
            <span> Belum ada log auto-sync tercatat.</span>
          )}
        </div>
      </div>
      )}

      {/* Period-based Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold dark:text-slate-400">Bulan Ini (IDR)</p>
                <p className="text-lg sm:text-xl font-bold text-cyan-700 mt-1 dark:text-cyan-300">{formatCurrency(stats.thisMonth)}</p>
                <p className="text-xs text-slate-400 mt-0.5 dark:text-slate-500">{successPayments.filter(p => paidAt(p) >= thisMonthStart).length} transaksi</p>
              </div>
              <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold dark:text-slate-400">Bulan Sebelumnya (IDR)</p>
                <p className="text-lg sm:text-xl font-bold text-emerald-700 mt-1 dark:text-emerald-300">{formatCurrency(stats.previousMonth)}</p>
                <p className="text-xs text-slate-400 mt-0.5 dark:text-slate-500">{successPayments.filter(p => { const d = paidAt(p); return d >= prevMonthStart && d <= prevMonthEnd; }).length} transaksi</p>
              </div>
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold dark:text-slate-400">Tahun Ini (IDR)</p>
                <p className="text-lg sm:text-xl font-bold text-amber-700 mt-1 dark:text-amber-300">{formatCurrency(stats.thisYear)}</p>
                <p className="text-xs text-slate-400 mt-0.5 dark:text-slate-500">{successPayments.filter(p => paidAt(p) >= thisYearStart).length} transaksi</p>
              </div>
              <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-rose-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold dark:text-slate-400">Pending / Failed</p>
                <p className="text-lg sm:text-2xl font-bold text-rose-600 mt-1 dark:text-rose-400">{stats.inactive}</p>
                <p className="text-xs text-slate-400 mt-0.5 dark:text-slate-500">butuh sync/tindakan</p>
              </div>
              <XCircle className="w-6 h-6 sm:w-8 sm:h-8 text-rose-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                <Input
                  placeholder="Cari nama, invoice, payment code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px] h-10 text-sm" data-testid="payment-status-filter">
                  <SelectValue placeholder="Semua Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Status</SelectItem>
                  <SelectItem value="success">Lunas</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failed">Gagal</SelectItem>
                  <SelectItem value="expired">Kedaluwarsa/Batal</SelectItem>
                </SelectContent>
              </Select>
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="w-full sm:w-[200px] h-10 text-sm" data-testid="payment-channel-filter">
                  <SelectValue placeholder="Semua Channel" />
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  <SelectItem value="all">Semua Channel</SelectItem>
                  {uniqueChannels.map(ch => {
                    const key = String(ch).toUpperCase();
                    const meta = CHANNEL_META[key] || CHANNEL_META[ch];
                    const label = meta ? meta.name : ch;
                    return <SelectItem key={ch} value={ch}>{label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto -mx-px">
            <table className="w-full min-w-[900px]">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  {canManage && (
                  <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 w-10">
                    <input
                      type="checkbox"
                      checked={isAllPageSelected}
                      onChange={toggleSelectAllPage}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer dark:border-slate-600 dark:text-indigo-400"
                      data-testid="select-all-checkbox"
                      aria-label="Pilih semua di halaman ini"
                    />
                  </th>
                  )}
                  
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => handleSort('status')}>Status{getSortIcon('status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => handleSort('customer_name')}>Nama{getSortIcon('customer_name')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => handleSort('account_number')} data-testid="th-id-pelanggan">ID Pelanggan{getSortIcon('account_number')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => handleSort('invoice_number')}>Invoice{getSortIcon('invoice_number')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => handleSort('plan_name')}>Paket{getSortIcon('plan_name')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => handleSort('payment_channel')}>Channel{getSortIcon('payment_channel')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => handleSort('payment_code')}>Payment Code{getSortIcon('payment_code')}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => handleSort('amount')}>Amount{getSortIcon('amount')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => handleSort('created_at')}>Created{getSortIcon('created_at')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => handleSort('tenant_id')}>Tenant{getSortIcon('tenant_id')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {paginatedPayments.length === 0 ? (
                  <tr>
                    <td colSpan={canManage ? 12 : 11} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                      {searchTerm || statusFilter !== 'all' || channelFilter !== 'all'
                        ? 'Tidak ada data yang sesuai filter'
                        : 'Belum ada transaksi pembayaran online'}
                    </td>
                  </tr>
                ) : (
                  paginatedPayments.map((payment, index) => (
                    <tr key={payment.id || index} className={`hover:bg-slate-50 dark:hover:bg-slate-800 ${selectedIds.has(payment.id) ? 'bg-indigo-50/50 dark:bg-indigo-950/20' : ''}`}>
                      {canManage && (
                      <td className="px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(payment.id)}
                          onChange={() => toggleSelect(payment.id)}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer dark:border-slate-600 dark:text-indigo-400"
                          data-testid={`select-row-${payment.id}`}
                          aria-label={`Pilih pembayaran ${payment.invoice_number || payment.id}`}
                        />
                      </td>
                      )}

                      <td className="px-4 py-3">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-white">
                        {payment.customer_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200 font-mono" data-testid="td-id-pelanggan">
                        {payment.account_number || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                        {payment.invoice_number || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                        {payment.plan_name ? (
                          <div className="flex flex-col gap-1">
                            <span className="inline-block px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium dark:bg-indigo-900/30 dark:text-indigo-300 w-fit">
                              {payment.plan_name}
                            </span>
                            {payment.voucher_code && (
                              <button
                                type="button"
                                onClick={() => { navigator.clipboard?.writeText(payment.voucher_code); toast.success('Kode voucher disalin: ' + payment.voucher_code); }}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-mono font-semibold dark:bg-emerald-900/30 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-800/40 transition w-fit"
                                data-testid={`voucher-code-${payment.id}`}
                                title="Klik untuk salin kode voucher"
                              >
                                <Copy className="w-3 h-3" />
                                {payment.voucher_code}
                              </button>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        {getChannelBadge(payment.payment_channel)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 font-mono">
                        {payment.payment_code || payment.payment_gateway_ref || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-slate-800 dark:text-white">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                        {formatDate(payment.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                        {payment.tenant_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {canManage && payment.status === 'pending' && (
                            <button
                              onClick={() => syncSinglePayment(payment.id)}
                              disabled={syncingId === payment.id}
                              className="p-1.5 rounded border border-amber-400 text-amber-600 hover:bg-amber-50 bg-white disabled:opacity-50 dark:text-amber-400 dark:hover:bg-amber-900/30 dark:bg-slate-800"
                              title="Sync dari Xendit"
                            >
                              {syncingId === payment.id ? (
                                <RotateCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <RotateCw className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => viewPaymentDetail(payment)}
                            className="p-1.5 rounded border border-blue-400 text-blue-600 hover:bg-blue-50 bg-white dark:text-blue-400 dark:hover:bg-blue-900/30 dark:bg-slate-800"
                            title="Lihat Detail"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {canManage && (
                          <button
                            onClick={() => deletePayment(payment.id)}
                            disabled={deletingId === payment.id}
                            className="p-1.5 rounded border border-red-400 text-red-600 hover:bg-red-50 bg-white disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/30 dark:bg-slate-800"
                            title="Hapus"
                          >
                            {deletingId === payment.id ? (
                              <RotateCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                          )}
                          {['success', 'paid', 'PAID'].includes(payment.status) && (
                            <span
                              className="p-1.5 rounded border border-green-400 text-green-600 bg-white dark:text-green-400 dark:bg-slate-800"
                              title="Sukses"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredPayments.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Tampilkan</span>
                  <Select
                    value={String(itemsPerPage)}
                    onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}
                  >
                    <SelectTrigger className="w-[80px] h-9 text-sm" data-testid="page-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-slate-600 dark:text-slate-400">entri</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredPayments.length)} dari {filteredPayments.length}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 rounded text-sm font-medium ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border border-slate-300 text-slate-700 bg-white hover:bg-slate-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <span className="px-1">...</span>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        className="w-8 h-8 rounded text-sm font-medium border border-slate-300 text-slate-700 bg-white hover:bg-slate-200 dark:border-slate-600 dark:text-slate-200 dark:bg-slate-800"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Detail Pembayaran
            </DialogTitle>
          </DialogHeader>
          
          {selectedPayment && (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <span className="text-sm text-slate-600 dark:text-slate-400">Status</span>
                {getStatusBadge(selectedPayment.status)}
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1 dark:text-slate-400">Nama Customer</p>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white">
                    {selectedPayment.customer_name || '-'}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1 dark:text-slate-400">ID Pelanggan</p>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white font-mono">
                    {selectedPayment.account_number || '-'}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1 dark:text-slate-400">No. Invoice</p>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white">
                    {selectedPayment.invoice_number || '-'}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1 dark:text-slate-400">Payment Channel</p>
                  {getChannelBadge(selectedPayment.payment_channel)}
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1 dark:text-slate-400">Amount</p>
                  <p className="text-sm font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(selectedPayment.amount)}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg col-span-2">
                  <p className="text-xs text-slate-500 mb-1 dark:text-slate-400">Payment Reference</p>
                  <p className="text-sm font-mono text-slate-800 dark:text-white break-all">
                    {selectedPayment.payment_gateway_ref || '-'}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1 dark:text-slate-400">Payment Code</p>
                  <p className="text-sm font-mono text-slate-800 dark:text-white">
                    {selectedPayment.payment_code || '-'}
                  </p>
                </div>
                {selectedPayment.voucher_code && (
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg col-span-2 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-1 flex items-center justify-between">
                      <span className="uppercase tracking-wide font-semibold">Kode Voucher</span>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard?.writeText(selectedPayment.voucher_code); toast.success('Kode voucher disalin'); }}
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid="detail-copy-voucher-code"
                      >
                        <Copy className="w-3 h-3" /> Salin
                      </button>
                    </p>
                    <p className="text-lg font-mono font-bold text-emerald-800 dark:text-emerald-200 break-all" data-testid="detail-voucher-code">
                      {selectedPayment.voucher_code}
                    </p>
                  </div>
                )}
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1 dark:text-slate-400">Tenant</p>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white">
                    {selectedPayment.tenant_name || '-'}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1 dark:text-slate-400">Created</p>
                  <p className="text-sm text-slate-800 dark:text-white">
                    {formatDate(selectedPayment.created_at)}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1 dark:text-slate-400">Paid At</p>
                  <p className="text-sm text-slate-800 dark:text-white">
                    {selectedPayment.paid_at ? formatDate(selectedPayment.paid_at) : '-'}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                {selectedPayment.status === 'pending' && (
                  <Button
                    onClick={() => {
                      syncSinglePayment(selectedPayment.id);
                      setShowDetailModal(false);
                    }}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    <RotateCw className="w-4 h-4 mr-2" />
                    Sync Status
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Tutup
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnlinePaymentReport;
